/**
 * O interruptor da casa.
 *
 * **POR QUE ISTO EXISTE.** Havia cinco interruptores escritos à mão, cada um
 * repetindo trilho, botão e deslocamento como classes soltas. A validação
 * visual do PR #601 achou dois defeitos ali, com causas diferentes e a mesma
 * aparência — o botão branco do lado de fora da pílula. Corrigir cada um no seu
 * arquivo não impede o terceiro: a geometria estava reescrita a cada uso
 * (ISSUE-192).
 *
 * **A GEOMETRIA É ARITMÉTICA, NÃO CLASSE ESCRITA À MÃO.** O percurso do botão
 * é derivado do tamanho do trilho — `trilho - botão - 2 × folga` — em vez de
 * ser um `translate-x-6` que alguém conferiu de cabeça uma vez. Encolher o
 * trilho não pode mais deixar o botão para fora, e `flex-shrink-0` mora aqui
 * porque foi exatamente a falta dele que produziu um trilho de 21px onde o
 * desenho pedia 48.
 */
import * as React from 'react'
import * as SwitchPrimitive from '@radix-ui/react-switch'

import { cn } from '@/lib/utils'

/** Medidas em pixels. A folga sai da diferença entre a altura e o botão. */
const TAMANHOS = {
  md: { trilho: 48, altura: 24, botao: 20 },
  sm: { trilho: 40, altura: 20, botao: 16 },
} as const

const TONS = {
  indigo: 'data-[state=checked]:bg-indigo-500',
  emerald: 'data-[state=checked]:bg-emerald-500',
} as const

interface SwitchProps
  extends React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> {
  tamanho?: keyof typeof TAMANHOS
  tom?: keyof typeof TONS
}

const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  SwitchProps
>(({ className, tamanho = 'md', tom = 'indigo', ...props }, ref) => {
  const { trilho, altura, botao } = TAMANHOS[tamanho]
  const folga = (altura - botao) / 2
  const percurso = trilho - botao - folga * 2

  return (
    <SwitchPrimitive.Root
      ref={ref}
      style={{ width: trilho, height: altura, padding: folga }}
      className={cn(
        'inline-flex flex-shrink-0 cursor-pointer items-center rounded-full transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'data-[state=unchecked]:bg-gray-700',
        TONS[tom],
        className
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        style={
          {
            width: botao,
            height: botao,
            '--percurso': `${percurso}px`,
          } as React.CSSProperties
        }
        className="pointer-events-none block rounded-full bg-white shadow-sm transition-transform data-[state=unchecked]:translate-x-0 data-[state=checked]:translate-x-[var(--percurso)]"
      />
    </SwitchPrimitive.Root>
  )
})
Switch.displayName = 'Switch'

export { Switch, TAMANHOS as TAMANHOS_DO_INTERRUPTOR }
